import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntForm, Input, Modal, Switch } from 'antd';
import { z } from 'zod';
import { isValidCidr } from '@shared/utils/cidr';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import type { Translator } from '@/i18n/config';
import type { IpAllowlistEntry, IpAllowlistInput } from '../types';

const FORM_ID = 'ip-allowlist-form';

function makeSchema(t: Translator) {
  return z.object({
    cidr: z
      .string()
      .min(1, t('form.cidrRequired'))
      .max(64)
      .refine((v) => isValidCidr(v), t('form.cidrInvalid')),
    label: z.string().min(1, t('form.labelRequired')).max(120),
    enabled: z.boolean(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

interface IpAllowlistFormModalProps {
  open: boolean;
  editing: IpAllowlistEntry | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: IpAllowlistInput) => void;
  onUpdate: (id: string, input: IpAllowlistInput) => void;
}

export function IpAllowlistFormModal({
  open,
  editing,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: IpAllowlistFormModalProps) {
  const { t } = useTranslation(['ip-allowlist', 'common']);
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const isEdit = editing !== null;

  const defaultValues: Values = {
    cidr: editing?.cidr ?? '',
    label: editing?.label ?? '',
    enabled: editing?.enabled ?? true,
  };

  const onSubmit = (values: Values) => {
    const input: IpAllowlistInput = {
      cidr: values.cidr.trim(),
      label: values.label.trim(),
      enabled: values.enabled,
    };
    if (isEdit && editing) onUpdate(editing.id, input);
    else onCreate(input);
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      onCancel={onCancel}
      okText={isEdit ? t('form.save') : t('form.create')}
      cancelText={t('common:actions.cancel')}
      okButtonProps={{ htmlType: 'submit', form: FORM_ID, loading: saving }}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form
        id={FORM_ID}
        schema={schema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        options={{ values: defaultValues }}
      >
        <AntForm layout="vertical" component="div">
          <FormField name="cidr" label={t('form.cidr')} required help={t('form.cidrHint')}>
            <Input placeholder={t('form.cidrPlaceholder')} maxLength={64} />
          </FormField>
          <FormField name="label" label={t('form.label')} required>
            <Input placeholder={t('form.labelPlaceholder')} maxLength={120} />
          </FormField>
          <FormField name="enabled" label={t('form.enabled')} valueProp="checked">
            <Switch />
          </FormField>
        </AntForm>
      </Form>
    </Modal>
  );
}
